import { Component, OnInit, OnDestroy, EventEmitter, Output } from '@angular/core';
import { Subscription } from 'rxjs';
import { NotificationModel } from '../../../../../model/notification.model';
import { NotifificationService } from '../../../../../services/notification.service';
import { AlertNotificationsService } from '../../../../../services/alert-notifications.service';
import { ControlPanelService } from '../../clinic-control-panel/control-panel.service';
import { TranslateService } from '@ngx-translate/core';
import { DateUtils } from '../../../../utils/date-utils';

@Component({
  selector: 'app-notification-dropdown',
  templateUrl: './notification-dropdown.component.html',
  styleUrls: ['./notification-dropdown.component.scss'],
  standalone: false,
})
export class NotificationDropdownComponent implements OnInit, OnDestroy {
  isLoading: boolean = false;
  private subscriptions = new Subscription();
  @Output() closeDropdown = new EventEmitter<void>();

  constructor(
    private notifService: NotifificationService,
    private alertNotifications: AlertNotificationsService,
    private controlPanelService: ControlPanelService,
    private translateService: TranslateService,
  ) { }

  ngOnInit() {
    this.loadNotifications();
    this.subscribeToLiveNotifications();
    this.notifService.init();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  private subscribeToLiveNotifications() {
    this.subscriptions.add(
      this.notifService.onNewRequest.subscribe(async () => {
        await this.handleNewRequestNotification();
      })
    );

    this.subscriptions.add(
      this.notifService.onNewContactRequest.subscribe(async () => {
        await this.handleNewContactRequest();
      })
    );

    this.subscriptions.add(
      this.notifService.onNewQuoteRequest.subscribe(async () => {
        await this.handleNewQuoteRequest();
      })
    );

    this.subscriptions.add(
      this.notifService.onNewQuoteInterest.subscribe(async () => {
        await this.handleNewQuoteInterest();
      })
    );
    this.subscriptions.add(
      this.notifService.onPartnerCreated.subscribe(async () => {
        await this.handlePartnerCreated();
      })
    );

    this.subscriptions.add(
      this.notifService.onPartnerUpdated.subscribe(async () => {
        await this.handlePartnerUpdated();
      })
    );
  }

  private async handleNewRequestNotification() {
    await this.loadNotifications();
  }

  private async handleNewContactRequest() {
    await this.loadNotifications();
  }

  private async handleNewQuoteRequest() {
    await this.loadNotifications();
  }

  private async handleNewQuoteInterest() {
    await this.loadNotifications();
  }

  private async handlePartnerCreated() {
    await this.loadNotifications();
  }
  private async handlePartnerUpdated() {
    await this.loadNotifications();
  }

  async loadNotifications() {
    this.isLoading = true;
    try {
      await this.notifService.getNotifications(0, 100, false);
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      this.isLoading = false;
    }
  }

  async markAsRead(notification: NotificationModel) {
    if (notification.readAt) return;
    try {
      await this.notifService.markAsRead(notification.id);
      this.controlPanelService.getCounters();
    } catch (error) {
      console.error('Error marking as read:', error);
      this.alertNotifications.error(this.translateService.instant('Failed to mark notification as read'));
    }
  }

  async markAllAsRead() {
    if (this.notifications.length === 0) return;

    try {
      await this.notifService.markAllAsRead();
      this.controlPanelService.getCounters();
    } catch (error) {
      console.error('Error marking all as read:', error);
      this.alertNotifications.error(this.translateService.instant('Failed to mark all as read'));
    }
  }

  trackByNotificationId(
    index: number,
    notification: NotificationModel
  ): number {
    return notification.id;
  }

  getRelativeTime(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    }
    if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    }
    if (diffInSeconds < 2592000) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days > 1 ? 's' : ''} ago`;
    }
    return DateUtils.formatDateOnly(date);
  }

  get notifications(): NotificationModel[] {
    return this.notifService.notifications;
  }
}